import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  adminAssignLeadAgent,
  adminBulkAssignLeadAgent,
  adminBulkImportLeads,
  adminBulkUpdateLeadStatus,
  adminConvertLead,
  adminEditLead,
  adminEditLeadNotes,
  adminResendLead,
  adminUpdateLeadStatus,
  agentSubmitInvite,
  getInviteRequests,
  getUsers,
} from '../../services/adminService';
import ConfirmationModal from '../../components/Admin/ConfirmationModal/ConfirmationModal';

const MAX_REQUEST_CHARS = 500;
const MAX_NOTES_CHARS = 2000;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const STATUSES = ['new', 'contacted', 'converted', 'rejected'];

const NewLeadInline = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [request, setRequest] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) {
    return (
      <button
        type="button"
        className="admin-button"
        onClick={() => setOpen(true)}
      >
        + Adaugă lead nou
      </button>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await agentSubmitInvite({ email, phone, name, request });
      if (res?.success) {
        setEmail('');
        setPhone('');
        setName('');
        setRequest('');
        setResult({ success: true, message: res.message || 'Lead adăugat.' });
        onCreated?.();
      } else {
        setResult({ success: false, message: res?.message || 'A apărut o eroare.' });
      }
    } catch (err) {
      setResult({
        success: false,
        message: err.response?.data?.message || 'Eroare la salvarea lead-ului.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="admin-section"
      style={{ background: '#f0f4ff', border: '1px solid #c7d2fe' }}
    >
      <div className="admin-section-header">
        <h2>Lead nou (din call center)</h2>
        <button
          type="button"
          className="admin-button secondary"
          onClick={() => setOpen(false)}
        >
          Anulează
        </button>
      </div>
      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>
            Email *
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, marginTop: 4 }}
            />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600 }}>
            Telefon (opțional)
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, marginTop: 4 }}
            />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600 }}>
            Nume Prenume / Nume Companie (opțional)
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, marginTop: 4 }}
            />
          </label>
        </div>
        <label style={{ fontSize: 13, fontWeight: 600 }}>
          Solicitare (opțional, max {MAX_REQUEST_CHARS})
          <textarea
            rows={3}
            value={request}
            onChange={(e) => setRequest(e.target.value.slice(0, MAX_REQUEST_CHARS))}
            style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, marginTop: 4, fontFamily: 'inherit', resize: 'vertical' }}
          />
          <small style={{ color: '#94a3b8' }}>
            {MAX_REQUEST_CHARS - request.length} caractere rămase
          </small>
        </label>
        {result && (
          <div
            style={{
              padding: 10,
              fontSize: 13,
              borderRadius: 8,
              background: result.success ? '#ecfdf5' : '#fef2f2',
              color: result.success ? '#047857' : '#b91c1c',
              border: `1px solid ${result.success ? '#a7f3d0' : '#fecaca'}`,
            }}
          >
            {result.message}
          </div>
        )}
        <button
          type="submit"
          className="admin-button"
          disabled={submitting || !email}
        >
          {submitting ? 'Se salvează…' : 'Salvează & trimite invitație'}
        </button>
      </form>
    </div>
  );
};

// ---------- Children-slot panels for the various ConfirmationModal actions --

const inputStyle = {
  display: 'block',
  width: '100%',
  padding: '6px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  marginTop: 4,
  fontSize: 13,
  fontFamily: 'inherit',
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#334155', display: 'block' };

const EditLeadFields = ({ draft, setDraft, submitting }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={labelStyle}>
      Email
      <input
        type="email"
        value={draft.email}
        onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        disabled={submitting}
        style={inputStyle}
      />
    </label>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      <label style={labelStyle}>
        Telefon
        <input
          type="tel"
          value={draft.phone}
          onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
          disabled={submitting}
          style={inputStyle}
        />
      </label>
      <label style={labelStyle}>
        Nume / Companie
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          disabled={submitting}
          style={inputStyle}
        />
      </label>
    </div>
    <label style={labelStyle}>
      UTM source
      <input
        type="text"
        value={draft.utmSource}
        onChange={(e) => setDraft({ ...draft, utmSource: e.target.value })}
        disabled={submitting}
        style={inputStyle}
        placeholder="ex: google_ads_2026q2"
      />
    </label>
    <label style={labelStyle}>
      Solicitare (max {MAX_REQUEST_CHARS})
      <textarea
        rows={3}
        value={draft.request}
        onChange={(e) =>
          setDraft({ ...draft, request: e.target.value.slice(0, MAX_REQUEST_CHARS) })
        }
        disabled={submitting}
        style={{ ...inputStyle, resize: 'vertical' }}
      />
    </label>
  </div>
);

const AgentPicker = ({ admins, loading, value, onChange, submitting }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <label style={labelStyle}>Agent</label>
    {loading ? (
      <small style={{ color: '#94a3b8' }}>Se încarcă administratorii…</small>
    ) : (
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={submitting}
        style={{ ...inputStyle, padding: '8px 10px' }}
      >
        <option value="">— Dezasignează —</option>
        {admins.map((a) => (
          <option key={a.id} value={a.id}>
            {(a.name || a.email)} ({a.email})
          </option>
        ))}
      </select>
    )}
  </div>
);

const NotesEditor = ({ draft, setDraft, submitting }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          type="radio"
          checked={draft.mode === 'append'}
          onChange={() => setDraft({ ...draft, mode: 'append' })}
          disabled={submitting}
        />
        Adaugă (cu timestamp)
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          type="radio"
          checked={draft.mode === 'replace'}
          onChange={() => setDraft({ ...draft, mode: 'replace' })}
          disabled={submitting}
        />
        Înlocuiește
      </label>
    </div>
    <label style={labelStyle}>
      Conținut notă (max {MAX_NOTES_CHARS})
      <textarea
        rows={5}
        value={draft.notes}
        onChange={(e) =>
          setDraft({ ...draft, notes: e.target.value.slice(0, MAX_NOTES_CHARS) })
        }
        disabled={submitting}
        style={{ ...inputStyle, resize: 'vertical' }}
        placeholder="ex: Sunat 12:30 — interesat de plan business, revin vineri."
      />
    </label>
  </div>
);

const ConvertPicker = ({ draft, setDraft, submitting, lookupUsers, lookupLoading }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={labelStyle}>
      Caută utilizator (email/nume/companie)
      <input
        type="search"
        value={draft.query}
        onChange={(e) => setDraft({ ...draft, query: e.target.value })}
        disabled={submitting}
        style={inputStyle}
        placeholder="ex: ionescu@ex.ro"
      />
    </label>
    <div
      style={{
        maxHeight: 180,
        overflow: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        fontSize: 13,
      }}
    >
      {lookupLoading ? (
        <div style={{ padding: 10, color: '#94a3b8' }}>Se caută…</div>
      ) : lookupUsers.length === 0 ? (
        <div style={{ padding: 10, color: '#94a3b8' }}>
          {draft.query ? 'Nici un utilizator găsit.' : 'Introdu un criteriu de căutare.'}
        </div>
      ) : (
        lookupUsers.map((u) => (
          <button
            key={u.id}
            type="button"
            onClick={() => setDraft({ ...draft, linkedUserId: u.id, query: u.email })}
            disabled={submitting}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '6px 10px',
              border: 0,
              background: draft.linkedUserId === u.id ? '#eef2ff' : 'transparent',
              cursor: 'pointer',
            }}
          >
            <strong>{u.email}</strong>
            {u.name ? ` — ${u.name}` : ''}
            {u.company ? ` (${u.company})` : ''}
          </button>
        ))
      )}
    </div>
    <label style={labelStyle}>
      Sau introdu direct ID-ul utilizatorului
      <input
        type="text"
        value={draft.linkedUserId}
        onChange={(e) => setDraft({ ...draft, linkedUserId: e.target.value })}
        disabled={submitting}
        style={inputStyle}
        placeholder="ObjectId (24 hex)"
      />
    </label>
  </div>
);

// ---------- CSV parsing helpers --------------------------------------------

const CSV_COLUMNS = ['email', 'phone', 'name', 'request', 'utmsource', 'notes', 'status'];

// Split one logical CSV line. Handles double-quoted fields with escaped quotes,
// and both comma and semicolon as the delimiter. The delimiter is picked once
// per file from the header row (see parseCsv below).
const splitCsvLine = (line, delimiter) => {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
};

const detectDelimiter = (headerLine) => {
  const commas = (headerLine.match(/,/g) || []).length;
  const semis = (headerLine.match(/;/g) || []).length;
  return semis > commas ? ';' : ',';
};

const normalizeHeader = (h) => (h || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Parse a CSV/TXT file string into { rows, errors }.
 * - rows: [{ email, phone?, name?, request?, utmSource?, notes?, status?, _line }]
 *   only includes rows with a non-empty email.
 * - errors: ["rândul X: email lipsește", ...]
 */
const parseCsv = (text) => {
  const cleaned = (text || '').replace(/^﻿/, '');
  const lines = cleaned.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { rows: [], errors: ['Fișierul este gol.'], totalParsed: 0 };

  const delimiter = detectDelimiter(lines[0]);
  const rawHeaders = splitCsvLine(lines[0], delimiter).map(normalizeHeader);
  const indexOf = (key) => rawHeaders.indexOf(key);

  if (indexOf('email') === -1) {
    return {
      rows: [],
      errors: ['Coloana "Email" lipsește din antet.'],
      totalParsed: 0,
    };
  }

  const colMap = {
    email: indexOf('email'),
    phone: indexOf('phone'),
    name: indexOf('name'),
    request: indexOf('request'),
    utmSource: indexOf('utmsource'),
    notes: indexOf('notes'),
    status: indexOf('status'),
  };

  const rows = [];
  const errors = [];
  let totalParsed = 0;

  for (let i = 1; i < lines.length; i += 1) {
    const cells = splitCsvLine(lines[i], delimiter);
    totalParsed += 1;
    const get = (key) => {
      const idx = colMap[key];
      if (idx < 0) return '';
      return (cells[idx] || '').trim();
    };
    const email = get('email');
    if (!email) {
      errors.push(`rândul ${i + 1}: email lipsește`);
      continue;
    }
    const row = { Email: email, _line: i + 1 };
    const phone = get('phone');
    const name = get('name');
    const request = get('request');
    const utmSource = get('utmSource');
    const notes = get('notes');
    const status = get('status');
    if (phone) row.Phone = phone;
    if (name) row.Name = name;
    if (request) row.Request = request;
    if (utmSource) row.UtmSource = utmSource;
    if (notes) row.Notes = notes;
    if (status) row.Status = status.toLowerCase();
    rows.push(row);
  }

  return { rows, errors, totalParsed };
};

// ---------- Tristate select-all checkbox -----------------------------------

const TristateCheckbox = ({ checked, indeterminate, onChange, disabled, title }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      title={title}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
    />
  );
};

// ---------- Per-row action menu --------------------------------------------

const RowActions = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="admin-button secondary"
        style={{ padding: '4px 10px', fontSize: 12 }}
        onClick={() => setOpen((v) => !v)}
      >
        Acțiuni ▾
      </button>
      {open && (
        <>
          <div
            onClick={close}
            style={{ position: 'fixed', inset: 0, zIndex: 10 }}
          />
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: 4,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              boxShadow: '0 8px 24px -8px rgba(15, 23, 42, 0.2)',
              minWidth: 200,
              zIndex: 11,
            }}
          >
            {[
              { key: 'edit', label: 'Editează' },
              { key: 'status', label: 'Schimbă status' },
              { key: 'agent', label: 'Asignează agent' },
              { key: 'notes', label: 'Notițe' },
              { key: 'convert', label: 'Marchează convertit' },
              { key: 'resend', label: 'Retrimite invitația' },
            ].map((it) => (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  close();
                  onPick(it.key);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  border: 0,
                  background: 'transparent',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ---------- Main page ------------------------------------------------------

const emptyEditDraft = (lead = {}) => ({
  email: lead.email || lead.Email || '',
  phone: lead.phone || lead.Phone || '',
  name: lead.name || lead.Name || '',
  request: lead.request || lead.Request || '',
  utmSource: lead.utmSource || lead.UtmSource || '',
});

const AdminLeads = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // pendingAction = { kind: 'edit'|'status'|'agent'|'notes'|'convert'|'resend', lead, ... }
  const [pendingAction, setPendingAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Edit draft
  const [editDraft, setEditDraft] = useState(emptyEditDraft());

  // Status draft
  const [statusDraft, setStatusDraft] = useState('new');

  // Agent picker state
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [agentDraft, setAgentDraft] = useState(null);

  // Notes draft
  const [notesDraft, setNotesDraft] = useState({ notes: '', mode: 'append' });

  // Convert draft (linked-user picker)
  const [convertDraft, setConvertDraft] = useState({ query: '', linkedUserId: '' });
  const [lookupUsers, setLookupUsers] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);

  // ---------- Phase 7: bulk selection + bulk actions + CSV import ----------

  // Selection persists across page changes; key is the lead id.
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // Which bulk dialog is open: null | 'status' | 'agent' | 'import'.
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Bulk drafts.
  const [bulkStatusDraft, setBulkStatusDraft] = useState('new');
  const [bulkAgentDraft, setBulkAgentDraft] = useState(null);

  // Pool of admin + CRM users for the bulk agent picker.
  const [bulkAdmins, setBulkAdmins] = useState([]);
  const [bulkAdminsLoading, setBulkAdminsLoading] = useState(false);

  // CSV import state.
  const csvInputRef = useRef(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvRows, setCsvRows] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvTotalParsed, setCsvTotalParsed] = useState(0);
  const [csvIncludeEmail, setCsvIncludeEmail] = useState(false);
  const [csvParseError, setCsvParseError] = useState(null);

  const flashOk = (message) => {
    setFeedback({ type: 'ok', message });
    setTimeout(() => setFeedback(null), 5000);
  };
  const flashErr = (message) => setFeedback({ type: 'err', message });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getInviteRequests({ status: statusFilter || null, page, pageSize });
      setRows(data.leads || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Eroare la incarcarea lead-urilor.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  // Lazy-load admins when the agent-picker modal is opened.
  useEffect(() => {
    if (pendingAction?.kind !== 'agent') return;
    let cancelled = false;
    (async () => {
      try {
        setAdminsLoading(true);
        const data = await getUsers({ role: 'Admin', page: 1, pageSize: 200 });
        if (!cancelled) setAdmins(data?.users || []);
      } catch {
        if (!cancelled) setAdmins([]);
      } finally {
        if (!cancelled) setAdminsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingAction?.kind]);

  // Debounced lookup for convert picker.
  useEffect(() => {
    if (pendingAction?.kind !== 'convert') return undefined;
    const q = convertDraft.query.trim();
    if (q.length < 2) {
      setLookupUsers([]);
      return undefined;
    }
    let cancelled = false;
    setLookupLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await getUsers({ query: q, page: 1, pageSize: 8 });
        if (!cancelled) setLookupUsers(data?.users || []);
      } catch {
        if (!cancelled) setLookupUsers([]);
      } finally {
        if (!cancelled) setLookupLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [convertDraft.query, pendingAction?.kind]);

  // Lazy-load admins + CRM users when the bulk agent dialog opens.
  useEffect(() => {
    if (bulkAction !== 'agent') return undefined;
    let cancelled = false;
    (async () => {
      try {
        setBulkAdminsLoading(true);
        const [adminRes, crmRes] = await Promise.all([
          getUsers({ role: 'Admin', page: 1, pageSize: 200 }).catch(() => null),
          getUsers({ role: 'CRM', page: 1, pageSize: 200 }).catch(() => null),
        ]);
        if (cancelled) return;
        const merged = new Map();
        for (const u of (adminRes?.users || [])) merged.set(u.id, u);
        for (const u of (crmRes?.users || [])) if (!merged.has(u.id)) merged.set(u.id, u);
        setBulkAdmins(Array.from(merged.values()));
      } catch {
        if (!cancelled) setBulkAdmins([]);
      } finally {
        if (!cancelled) setBulkAdminsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bulkAction]);

  // ---------- Selection helpers ------------------------------------------

  const visibleIds = useMemo(
    () => rows.map((r) => r.id || r.Id).filter(Boolean),
    [rows],
  );
  const visibleSelectedCount = useMemo(
    () => visibleIds.reduce((acc, id) => acc + (selectedIds.has(id) ? 1 : 0), 0),
    [visibleIds, selectedIds],
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
  const someVisibleSelected =
    visibleSelectedCount > 0 && visibleSelectedCount < visibleIds.length;

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) for (const id of visibleIds) next.add(id);
      else for (const id of visibleIds) next.delete(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // ---------- Bulk handlers -----------------------------------------------

  const openBulk = (kind) => {
    if (kind === 'status') setBulkStatusDraft('new');
    if (kind === 'agent') setBulkAgentDraft(null);
    if (kind === 'import') {
      setCsvFileName('');
      setCsvRows([]);
      setCsvErrors([]);
      setCsvTotalParsed(0);
      setCsvIncludeEmail(false);
      setCsvParseError(null);
    }
    setBulkAction(kind);
  };

  const closeBulk = () => {
    if (bulkSubmitting) return;
    setBulkAction(null);
  };

  const summarizeBulkResult = (res, fallback) => {
    if (!res) return fallback;
    const succeeded = res.succeeded ?? 0;
    const failed = (res.failed ?? 0) + (res.notFound ?? 0);
    return `${succeeded} reușite, ${failed} eșuate`;
  };

  const handleBulkStatus = async ({ reason }) => {
    setBulkSubmitting(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await adminBulkUpdateLeadStatus({
        ids,
        status: bulkStatusDraft,
        reason,
      });
      if (res?.success) {
        flashOk(`Status actualizat: ${summarizeBulkResult(res, '')}.`);
        setBulkAction(null);
        clearSelection();
        await load();
      } else {
        flashErr(res?.message || 'Acțiunea bulk a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkAgent = async ({ reason }) => {
    setBulkSubmitting(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await adminBulkAssignLeadAgent({
        ids,
        agentId: bulkAgentDraft,
        reason,
      });
      if (res?.success) {
        flashOk(
          `${bulkAgentDraft ? 'Agent asignat' : 'Agent dezasignat'}: ${summarizeBulkResult(res, '')}.`,
        );
        setBulkAction(null);
        clearSelection();
        await load();
      } else {
        flashErr(res?.message || 'Acțiunea bulk a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setBulkSubmitting(false);
    }
  };

  // ---------- CSV import --------------------------------------------------

  const handleCsvFile = async (file) => {
    if (!file) return;
    setCsvFileName(file.name);
    setCsvParseError(null);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      setCsvRows(parsed.rows);
      setCsvErrors(parsed.errors);
      setCsvTotalParsed(parsed.totalParsed);
    } catch (err) {
      setCsvParseError(err?.message || 'Eroare la citirea fișierului.');
      setCsvRows([]);
      setCsvErrors([]);
      setCsvTotalParsed(0);
    }
  };

  const handleBulkImport = async ({ reason }) => {
    setBulkSubmitting(true);
    try {
      // Strip the bookkeeping `_line` before sending to the BE.
      const payload = csvRows.map(({ _line, ...rest }) => rest);
      const res = await adminBulkImportLeads({
        rows: payload,
        reason,
        source: 'csv_import',
        includeEmail: csvIncludeEmail,
      });
      if (res?.success) {
        const succeeded = res.succeeded ?? 0;
        const failed = (res.failed ?? 0) + (res.notFound ?? 0);
        flashOk(`${succeeded} import-uri reușite, ${failed} eșuate.`);
        setBulkAction(null);
        await load();
      } else {
        flashErr(res?.message || 'Importul a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const openAction = (kind, lead) => {
    if (kind === 'edit') setEditDraft(emptyEditDraft(lead));
    if (kind === 'status') setStatusDraft(lead.status || lead.Status || 'new');
    if (kind === 'agent') setAgentDraft(lead.agentId || lead.AgentId || null);
    if (kind === 'notes') setNotesDraft({ notes: '', mode: 'append' });
    if (kind === 'convert') {
      setConvertDraft({
        query: lead.email || lead.Email || '',
        linkedUserId: lead.linkedUserId || lead.LinkedUserId || '',
      });
      setLookupUsers([]);
    }
    setPendingAction({ kind, lead });
  };

  const closeModal = () => {
    if (submitting) return;
    setPendingAction(null);
  };

  // ---------- Handlers ----------------------------------------------------

  const handleEdit = async ({ reason }) => {
    setSubmitting(true);
    try {
      const id = pendingAction.lead.id || pendingAction.lead.Id;
      const r = await adminEditLead(id, { ...editDraft, reason });
      if (r?.success) {
        flashOk('Lead-ul a fost actualizat.');
        setPendingAction(null);
        await load();
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatus = async ({ reason }) => {
    setSubmitting(true);
    try {
      const id = pendingAction.lead.id || pendingAction.lead.Id;
      const r = await adminUpdateLeadStatus(id, { status: statusDraft, reason });
      if (r?.success) {
        flashOk('Status-ul a fost actualizat.');
        setPendingAction(null);
        await load();
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAgent = async ({ reason }) => {
    setSubmitting(true);
    try {
      const id = pendingAction.lead.id || pendingAction.lead.Id;
      const r = await adminAssignLeadAgent(id, { agentId: agentDraft, reason });
      if (r?.success) {
        flashOk(agentDraft ? 'Agent asignat.' : 'Agent dezasignat.');
        setPendingAction(null);
        await load();
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNotes = async ({ reason }) => {
    setSubmitting(true);
    try {
      const id = pendingAction.lead.id || pendingAction.lead.Id;
      const r = await adminEditLeadNotes(id, {
        notes: notesDraft.notes,
        mode: notesDraft.mode,
        reason,
      });
      if (r?.success) {
        flashOk('Notițele au fost actualizate.');
        setPendingAction(null);
        await load();
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConvert = async ({ reason }) => {
    setSubmitting(true);
    try {
      const id = pendingAction.lead.id || pendingAction.lead.Id;
      const r = await adminConvertLead(id, {
        linkedUserId: convertDraft.linkedUserId.trim(),
        reason,
      });
      if (r?.success) {
        flashOk('Lead-ul a fost marcat convertit.');
        setPendingAction(null);
        await load();
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async ({ reason }) => {
    setSubmitting(true);
    try {
      const id = pendingAction.lead.id || pendingAction.lead.Id;
      const r = await adminResendLead(id, { reason });
      if (r?.success) {
        flashOk('Email-ul de invitație a fost retrimis.');
        setPendingAction(null);
      } else {
        flashErr(r?.message || 'Acțiunea a eșuat.');
      }
    } catch (err) {
      flashErr(err?.response?.data?.message || err?.message || 'Eroare neașteptată.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Conditional canSubmit flags ---------------------------------

  const canSubmitConvert = useMemo(
    () => /^[a-fA-F0-9]{24}$/.test(convertDraft.linkedUserId.trim()),
    [convertDraft.linkedUserId],
  );
  const canSubmitNotes = useMemo(
    () => notesDraft.notes.trim().length > 0,
    [notesDraft.notes],
  );
  const canSubmitEdit = useMemo(
    () => (editDraft.email || '').trim().length > 0,
    [editDraft.email],
  );
  const canSubmitBulkStatus = selectedIds.size > 0 && selectedIds.size <= 100;
  const canSubmitBulkAgent = selectedIds.size > 0 && selectedIds.size <= 100;
  const canSubmitBulkImport = csvRows.length > 0 && csvRows.length <= 100;

  const targetLead = pendingAction?.lead;
  const targetLabel = targetLead
    ? `${targetLead.email || targetLead.Email}${targetLead.name || targetLead.Name ? ` (${targetLead.name || targetLead.Name})` : ''}`
    : '';

  return (
    <>
      <NewLeadInline onCreated={load} />

      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Lead-uri (invitații)</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {selectedIds.size > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 10px',
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 999,
                  background: '#eef2ff',
                  color: '#4338ca',
                  border: '1px solid #c7d2fe',
                }}
              >
                {selectedIds.size} selectate
              </span>
            )}
            <button
              type="button"
              className="admin-button secondary"
              onClick={() => openBulk('import')}
            >
              Importă CSV
            </button>
          </div>
        </div>

        {feedback && (
          <div
            role="status"
            style={{
              padding: 10,
              fontSize: 13,
              borderRadius: 8,
              marginBottom: 10,
              background: feedback.type === 'err' ? '#fef2f2' : '#ecfdf5',
              color: feedback.type === 'err' ? '#b91c1c' : '#047857',
              border: `1px solid ${feedback.type === 'err' ? '#fecaca' : '#a7f3d0'}`,
            }}
          >
            {feedback.type === 'err' ? '⚠️ ' : '✓ '}
            {feedback.message}
          </div>
        )}

        <div className="admin-toolbar">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Toate status-urile</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        {selectedIds.size > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: 10,
              marginBottom: 10,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
            }}
          >
            <strong style={{ fontSize: 13, color: '#334155' }}>
              {selectedIds.size} selectate
            </strong>
            {selectedIds.size > 100 && (
              <span style={{ fontSize: 12, color: '#b91c1c' }}>
                (max 100 per acțiune)
              </span>
            )}
            <div style={{ flex: 1 }} />
            <button
              type="button"
              className="admin-button"
              onClick={() => openBulk('status')}
              disabled={selectedIds.size > 100}
            >
              Schimbă status
            </button>
            <button
              type="button"
              className="admin-button"
              onClick={() => openBulk('agent')}
              disabled={selectedIds.size > 100}
            >
              Asignează agent
            </button>
            <button
              type="button"
              className="admin-button secondary"
              onClick={clearSelection}
              title="Deselectează tot"
              style={{ color: '#b91c1c' }}
            >
              🗑 Deselectează tot
            </button>
          </div>
        )}

        {loading ? (
          <div className="admin-empty">Se încarcă…</div>
        ) : error ? (
          <div className="admin-empty">⚠️ {error}</div>
        ) : rows.length === 0 ? (
          <div className="admin-empty">Nu există lead-uri care să corespundă filtrului.</div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <TristateCheckbox
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected}
                      onChange={toggleAllVisible}
                      title="Selectează toate rândurile vizibile"
                    />
                  </th>
                  <th>Trimis</th>
                  <th>Email</th>
                  <th>Telefon</th>
                  <th>Nume/Companie</th>
                  <th>Sursă</th>
                  <th>Status</th>
                  <th>Solicitare</th>
                  <th>Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => {
                  const rowId = l.id || l.Id;
                  const isSelected = selectedIds.has(rowId);
                  return (
                  <tr key={rowId} style={isSelected ? { background: '#f5f7ff' } : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOne(rowId)}
                        aria-label="Selectează lead-ul"
                      />
                    </td>
                    <td>{fmtDate(l.submittedAt || l.SubmittedAt)}</td>
                    <td>{l.email || l.Email}</td>
                    <td>{l.phone || l.Phone || '—'}</td>
                    <td>{l.name || l.Name || '—'}</td>
                    <td>
                      <span className={`admin-source ${(l.source || l.Source || 'website').toLowerCase()}`}>
                        {(l.source || l.Source || 'website').toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`admin-status ${(l.status || l.Status || 'new').toLowerCase()}`}
                      >
                        {(l.status || l.Status || 'new').toUpperCase()}
                      </span>
                      {(l.contactedAt || l.ContactedAt) && (
                        <>
                          <br />
                          <small style={{ color: '#94a3b8' }}>
                            cont. {fmtDate(l.contactedAt || l.ContactedAt)}
                          </small>
                        </>
                      )}
                      {(l.convertedAt || l.ConvertedAt) && (
                        <>
                          <br />
                          <small style={{ color: '#047857' }}>
                            conv. {fmtDate(l.convertedAt || l.ConvertedAt)}
                          </small>
                        </>
                      )}
                    </td>
                    <td style={{ maxWidth: 280 }}>
                      <span title={l.request || l.Request}>
                        {(l.request || l.Request || '').slice(0, 90)}
                        {(l.request || l.Request || '').length > 90 ? '…' : ''}
                      </span>
                    </td>
                    <td>
                      <RowActions onPick={(kind) => openAction(kind, l)} />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="admin-pagination">
          <button
            className="admin-button secondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            ‹ Anterior
          </button>
          <span>Pagina {page}</span>
          <button
            className="admin-button secondary"
            onClick={() => setPage((p) => p + 1)}
            disabled={rows.length < pageSize || loading}
          >
            Următor ›
          </button>
        </div>
      </div>

      {/* ---- Per-action confirmation modals ---- */}

      <ConfirmationModal
        isOpen={pendingAction?.kind === 'edit'}
        onClose={closeModal}
        onConfirm={handleEdit}
        title="Editează lead-ul"
        description={`Modifică datele de identificare pentru ${targetLabel}. Câmpurile lăsate goale se șterg.`}
        actionLabel="Salvează"
        submitting={submitting}
        canSubmitExtra={canSubmitEdit}
      >
        {pendingAction?.kind === 'edit' && (
          <EditLeadFields draft={editDraft} setDraft={setEditDraft} submitting={submitting} />
        )}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={pendingAction?.kind === 'status'}
        onClose={closeModal}
        onConfirm={handleStatus}
        title="Schimbă status-ul lead-ului"
        description={`Mută ${targetLabel} într-un alt status al pâlniei.`}
        actionLabel="Salvează"
        submitting={submitting}
      >
        {pendingAction?.kind === 'status' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>Status</label>
            <select
              value={statusDraft}
              onChange={(e) => setStatusDraft(e.target.value)}
              disabled={submitting}
              style={{ ...inputStyle, padding: '8px 10px' }}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        )}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={pendingAction?.kind === 'agent'}
        onClose={closeModal}
        onConfirm={handleAgent}
        title="Asignează un agent"
        description={`Alege administratorul responsabil pentru ${targetLabel}.`}
        actionLabel="Salvează"
        submitting={submitting}
      >
        {pendingAction?.kind === 'agent' && (
          <AgentPicker
            admins={admins}
            loading={adminsLoading}
            value={agentDraft}
            onChange={setAgentDraft}
            submitting={submitting}
          />
        )}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={pendingAction?.kind === 'notes'}
        onClose={closeModal}
        onConfirm={handleNotes}
        title="Notițe interne"
        description={`Adaugă sau înlocuiește notițele pentru ${targetLabel}. Notițele nu sunt vizibile pentru lead.`}
        actionLabel="Salvează"
        submitting={submitting}
        canSubmitExtra={canSubmitNotes}
      >
        {pendingAction?.kind === 'notes' && (
          <NotesEditor draft={notesDraft} setDraft={setNotesDraft} submitting={submitting} />
        )}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={pendingAction?.kind === 'convert'}
        onClose={closeModal}
        onConfirm={handleConvert}
        title="Marchează lead-ul convertit"
        description={`Asociază ${targetLabel} cu un utilizator înregistrat. Status-ul devine "converted".`}
        actionLabel="Marchează convertit"
        submitting={submitting}
        canSubmitExtra={canSubmitConvert}
      >
        {pendingAction?.kind === 'convert' && (
          <ConvertPicker
            draft={convertDraft}
            setDraft={setConvertDraft}
            submitting={submitting}
            lookupUsers={lookupUsers}
            lookupLoading={lookupLoading}
          />
        )}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={pendingAction?.kind === 'resend'}
        onClose={closeModal}
        onConfirm={handleResend}
        title="Retrimite email-ul de invitație"
        description={`Va fi retrimis email-ul standard de invitație către ${targetLabel}.`}
        actionLabel="Retrimite"
        submitting={submitting}
      />

      {/* ---- Bulk action modals (Phase 7) ---- */}

      <ConfirmationModal
        isOpen={bulkAction === 'status'}
        onClose={closeBulk}
        onConfirm={handleBulkStatus}
        title="Schimbă status pentru lead-urile selectate"
        description={`Aplică același status la ${selectedIds.size} lead-uri.`}
        actionLabel="Aplică"
        submitting={bulkSubmitting}
        canSubmitExtra={canSubmitBulkStatus}
      >
        {bulkAction === 'status' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>Status nou</label>
            <select
              value={bulkStatusDraft}
              onChange={(e) => setBulkStatusDraft(e.target.value)}
              disabled={bulkSubmitting}
              style={{ ...inputStyle, padding: '8px 10px' }}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        )}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={bulkAction === 'agent'}
        onClose={closeBulk}
        onConfirm={handleBulkAgent}
        title="Asignează agent pentru lead-urile selectate"
        description={`Asignează (sau dezasignează) același agent pentru ${selectedIds.size} lead-uri.`}
        actionLabel="Aplică"
        submitting={bulkSubmitting}
        canSubmitExtra={canSubmitBulkAgent}
      >
        {bulkAction === 'agent' && (
          <AgentPicker
            admins={bulkAdmins}
            loading={bulkAdminsLoading}
            value={bulkAgentDraft}
            onChange={setBulkAgentDraft}
            submitting={bulkSubmitting}
          />
        )}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={bulkAction === 'import'}
        onClose={closeBulk}
        onConfirm={handleBulkImport}
        title="Importă lead-uri din CSV"
        description="Încarcă un fișier CSV/TXT. Coloana Email este obligatorie. Rândurile fără email sunt ignorate."
        actionLabel="Importă"
        submitting={bulkSubmitting}
        canSubmitExtra={canSubmitBulkImport}
      >
        {bulkAction === 'import' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                onChange={(e) => handleCsvFile(e.target.files?.[0])}
                disabled={bulkSubmitting}
                style={{ fontSize: 13 }}
              />
              {csvFileName && (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                  Fișier: <strong>{csvFileName}</strong>
                </div>
              )}
            </div>

            {csvParseError && (
              <div
                style={{
                  padding: 8,
                  fontSize: 12,
                  borderRadius: 6,
                  background: '#fef2f2',
                  color: '#b91c1c',
                  border: '1px solid #fecaca',
                }}
              >
                ⚠️ {csvParseError}
              </div>
            )}

            {csvTotalParsed > 0 && (
              <div style={{ fontSize: 13, color: '#334155' }}>
                <strong>{csvTotalParsed}</strong> rânduri parsate,{' '}
                <strong>{csvRows.length}</strong> valide
                {csvErrors.length > 0 && (
                  <span style={{ color: '#b91c1c' }}> ({csvErrors.length} erori)</span>
                )}
              </div>
            )}

            {csvRows.length > 100 && (
              <div style={{ fontSize: 12, color: '#b91c1c' }}>
                Maxim 100 rânduri per import. Împarte fișierul în loturi mai mici.
              </div>
            )}

            {csvErrors.length > 0 && (
              <div
                style={{
                  maxHeight: 100,
                  overflow: 'auto',
                  padding: 8,
                  fontSize: 12,
                  borderRadius: 6,
                  background: '#fef2f2',
                  color: '#b91c1c',
                  border: '1px solid #fecaca',
                }}
              >
                {csvErrors.slice(0, 20).map((msg, idx) => (
                  <div key={idx}>{msg}</div>
                ))}
                {csvErrors.length > 20 && (
                  <div>… și încă {csvErrors.length - 20}</div>
                )}
              </div>
            )}

            {csvRows.length > 0 && (
              <div
                style={{
                  maxHeight: 220,
                  overflow: 'auto',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr style={{ background: '#f1f5f9' }}>
                      <th style={{ padding: '4px 6px', textAlign: 'left' }}>#</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left' }}>Email</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left' }}>Telefon</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left' }}>Nume</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvRows.slice(0, 10).map((r, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '4px 6px', color: '#94a3b8' }}>{r._line}</td>
                        <td style={{ padding: '4px 6px' }}>{r.Email}</td>
                        <td style={{ padding: '4px 6px' }}>{r.Phone || '—'}</td>
                        <td style={{ padding: '4px 6px' }}>{r.Name || '—'}</td>
                        <td style={{ padding: '4px 6px' }}>{r.Status || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {csvRows.length > 10 && (
                  <div style={{ padding: 6, color: '#94a3b8', textAlign: 'center' }}>
                    … plus încă {csvRows.length - 10} rânduri
                  </div>
                )}
              </div>
            )}

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                color: '#334155',
              }}
            >
              <input
                type="checkbox"
                checked={csvIncludeEmail}
                onChange={(e) => setCsvIncludeEmail(e.target.checked)}
                disabled={bulkSubmitting}
              />
              Trimite email de invitație fiecărui contact
            </label>
          </div>
        )}
      </ConfirmationModal>
    </>
  );
};

export default AdminLeads;
